import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (localStorage.getItem("mp_admin_token")) navigate("/admin");
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("mp_admin_token", data.token);
      localStorage.setItem("mp_admin_name", data.name || "Admin");
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-[#3FE1D1]/10 blur-[150px]" />
      <div className="w-full max-w-md relative">
        <div className="flex items-center justify-center mb-8">
          <img src="/brand/mp-wordmark-white.png" alt="Monthly Pops" className="h-7" />
        </div>
        <div className="bg-[#131315] border border-[#262629] p-8 md:p-10">
          <div className="flex items-center gap-2 text-[#3FE1D1] mb-6">
            <Lock size={16} />
            <span className="text-xs uppercase tracking-[0.24em] font-semibold">Admin Access</span>
          </div>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-2xl mb-6">
            Consignment Inbox
          </h1>
          <form onSubmit={submit} className="space-y-4">
            <input
              data-testid="admin-email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#050505] border border-[#262629] text-white placeholder:text-white/30 px-4 py-3.5 text-sm focus:outline-none focus:border-[#3FE1D1] focus:ring-1 focus:ring-[#3FE1D1]"
            />
            <input
              data-testid="admin-password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#050505] border border-[#262629] text-white placeholder:text-white/30 px-4 py-3.5 text-sm focus:outline-none focus:border-[#3FE1D1] focus:ring-1 focus:ring-[#3FE1D1]"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-red-400 text-sm">{error}</p>
            )}
            <button
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#3FE1D1] text-[#050505] font-bold uppercase text-sm tracking-[0.14em] px-8 py-4 hover:bg-white transition-colors disabled:opacity-60"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign In"}
            </button>
          </form>
        </div>
        <button
          onClick={() => navigate("/")}
          className="mt-6 mx-auto block text-white/40 hover:text-white text-xs uppercase tracking-widest transition-colors"
        >
          ← Back to site
        </button>
      </div>
    </main>
  );
}
